// Guided product demo — real app in an iframe, narrator sidebar, callout overlays.
// Route: /demo
//
// Each step seeds localStorage (seller journey state) then navigates the iframe
// to a real app URL. No mocked screens — the actual product runs in the frame.
//
// Controls: ← → arrow keys, spacebar to pause, on-screen buttons.

import { useCallback, useEffect, useRef, useState } from "react";

// ─── Constants ────────────────────────────────────────────────────────────────
const BETA_SECRET  = process.env.REACT_APP_BETA_SECRET || "sixzero2026";
const BETA_KEY     = "od_beta_token";
const LISTINGS_KEY = "sz_seller_listings";
const CURRENT_KEY  = "sz_seller_current_id";

const DEMO_SELLER_ID = "demo0000-0000-0000-0000-seller000001";
const DEMO_TX_ID     = "a0eebc99-9c0b-4ef8-bb6d-6bb9bd380b11";
const DEMO_TX_TOKEN  = "a0eebc99-9c0b-4ef8-bb6d-6bb9bd380b12";
const DEMO_SL_ID     = "a0eebc99-9c0b-4ef8-bb6d-6bb9bd380a11";
const DEMO_SL_TOKEN  = "a0eebc99-9c0b-4ef8-bb6d-6bb9bd380a12";

// Pre-seeded stage data — avoids live AI calls during the demo
const DEMO_DISCLOSURES_DATA = {
  status: "done",
  data: {
    questions: [
      { id: "neighbor-disputes", q: "Have there been any disputes with neighbors about boundaries, noise, or access in the past 3 years?", section: "TDS", note_required_if_yes: true },
      { id: "unpermitted-work",  q: "Are there any additions, conversions, or improvements made without a permit?",                       section: "SPQ", note_required_if_yes: true },
      { id: "insurance-claims",  q: "Have you filed any homeowner's insurance claims in the past 5 years?",                               section: "SPQ", note_required_if_yes: true },
      { id: "mold-history",      q: "Has the property ever had mold, mildew, or water intrusion not captured in the inspection report?",  section: "TDS", note_required_if_yes: true },
      { id: "litigation",        q: "Is the property involved in any current or pending litigation?",                                      section: "SPQ", note_required_if_yes: true },
      { id: "hoa-issues",        q: "Are there any pending HOA special assessments or known rule violations?",                            section: "HOA", note_required_if_yes: false },
      { id: "lead-paint",        q: "Do you have knowledge of lead-based paint or lead-based paint hazards in the home?",                 section: "LEAD", note_required_if_yes: true },
    ],
    answers: {
      "neighbor-disputes": { answer: "no" },
      "unpermitted-work":  { answer: "yes", note: "Converted garage storage area — no structural changes" },
      "insurance-claims":  { answer: "no" },
      "mold-history":      { answer: "no" },
      "litigation":        { answer: "no" },
      "hoa-issues":        { answer: "no" },
      "lead-paint":        { answer: "yes", note: "Pre-1978 home; no known hazards, no remediation done" },
    },
    summary: "**Summary**\nDisclosing one unpermitted improvement (garage storage conversion) and the standard lead-paint acknowledgment required for pre-1978 construction. No litigation, insurance claims, neighbor disputes, or HOA issues to report.\n\n**By section**\n- **TDS:** No known neighbor disputes or water intrusion history beyond inspection findings.\n- **SPQ:** One unpermitted improvement: garage storage area conversion, no structural changes. No insurance claims or litigation.\n- **NHD:** No natural hazard disclosures triggered by this interview. Standard NHD report will be ordered separately.\n- **Lead paint:** Pre-1978 construction — lead-paint disclosure required. Seller has no knowledge of active hazards.\n- **HOA:** N/A — no HOA on this property.\n\n**Items that may affect price or buyer interest**\n- Unpermitted garage conversion: buyers may request permit history or a credit at closing.\n- Lead paint disclosure required (pre-1978); standard 10-day buyer inspection right applies.",
    generated_at: "2026-05-30T12:00:00Z",
  },
};

const DEMO_OFFERS_DATA = {
  status: "done",
  data: {
    mode: "diy",
    offers: [
      { id: "A82", price: 1395000, close_days: 30, financing: "30% down conventional, 21d financing contingency", emd: 40000, inspection_contingency: "17 days", appraisal_contingency: "yes" },
      { id: "B11", price: 1420000, close_days: 21, financing: "All cash", emd: 50000, inspection_contingency: "10 days", appraisal_contingency: "waived" },
    ],
    analysis: "**Side-by-side**\n- Offer A82: $1,395,000 · 30% down conventional · close-probability **72%** · risk-weighted net ~$1,337,000 (21d financing contingency exposure)\n- Offer B11: $1,420,000 · All cash · close-probability **96%** · risk-weighted net ~$1,363,000 — no financing contingency, 10d inspection only\n\n**Recommended strategy**\nAccept Offer B11 (all-cash, $1,420,000). The $25,000 premium, waived appraisal contingency, and 96% close probability make B11 decisively stronger. Counter A82 only if B11 withdraws before acceptance.\n\n**Counter terms for Offer A82**\n- Raise price to $1,420,000 to match the all-cash offer\n- Shorten financing contingency from 21 to 14 days\n- Waive appraisal contingency (pre-listing AI estimate at $1,210,000 supports this)\n- Reduce inspection period to 10 days",
    generated_at: "2026-05-30T14:00:00Z",
  },
};

// ─── Seller localStorage seeder ──────────────────────────────────────────────
function makeDemoJourney(stageIdx, stageOverrides = {}) {
  return {
    id: DEMO_SELLER_ID,
    address: "742 Evergreen Terrace, Springfield, CA 94105",
    property: {
      property: [{
        building: {
          rooms: { beds: 3, bathstotal: 2 },
          size:  { bldgsize: 1640 },
          yearbuilt: 1962,
        },
        lot: { lotsize2: 5500 },
      }],
    },
    verification: { owner_of_record: true, is_self: true, relationship: null, confirmed_at: "2026-05-01T10:00:00Z" },
    currentStageIdx: stageIdx,
    sessionId: null,
    stages: {
      valuation: {
        status: "done",
        data: {
          text: "**Fair listing-price range: $1,180,000 – $1,240,000**\n\n**Recommended list price:** $1,210,000\n\n**Why this range:**\n- 743 Maple Ave sold for $1,195,000 ($742/sqft) — 3 months ago\n- 29 Oak Street sold for $1,220,000 ($758/sqft) — 6 weeks ago\n- Active comp at $1,250,000 sat 42 days — above market signal\n- Your home's 1,640 sqft at list price = $738/sqft, in line\n\n**Confidence:** High — three recent closes within 0.4 miles, tight range.",
          range: { low: 1180000, high: 1240000 },
          generated_at: "2026-05-01T10:00:00Z",
        },
      },
      prep: {
        status: "done",
        data: {
          roi_items: [
            { id: "floors",   task: "Refinish hardwood floors",          cost_est: "$900–$1,400",   value_lift: "~$8,000", priority: "high", why: "Buyers discount heavily for worn floors — ROI is 5–8×." },
            { id: "paint",    task: "Fresh neutral paint throughout",    cost_est: "$1,200–$2,000", value_lift: "~$6,000", priority: "high", why: "Freshly painted homes sell 12% faster and for 3% more." },
            { id: "hardware", task: "Update kitchen cabinet hardware",   cost_est: "$200–$400",     value_lift: "~$3,500", priority: "med",  why: "Small visual upgrade with outsized perceived value." },
            { id: "lighting", task: "Replace dated light fixtures",      cost_est: "$300–$600",     value_lift: "~$2,000", priority: "med",  why: "Modern fixtures photograph well and signal maintenance." },
            { id: "pressure", task: "Pressure-wash driveway & walkway", cost_est: "$150–$300",     value_lift: "~$1,500", priority: "low",  why: "First impression drives the initial offer anchor." },
          ],
        },
      },
      ...stageOverrides,
    },
    created_at: "2026-05-01T10:00:00Z",
    last_opened_at: new Date().toISOString(),
  };
}

function seedSeller(stageIdx, stageOverrides) {
  localStorage.setItem(BETA_KEY, BETA_SECRET);
  const journey = makeDemoJourney(stageIdx, stageOverrides);
  localStorage.setItem(LISTINGS_KEY, JSON.stringify({ [DEMO_SELLER_ID]: journey }));
  localStorage.setItem(CURRENT_KEY, DEMO_SELLER_ID);
}

function seedBuyer() {
  localStorage.setItem(BETA_KEY, BETA_SECRET);
}

function clearSellerCurrent() {
  localStorage.setItem(BETA_KEY, BETA_SECRET);
  localStorage.setItem(LISTINGS_KEY, JSON.stringify({}));
  localStorage.removeItem(CURRENT_KEY);
}

// ─── Chapter colors ───────────────────────────────────────────────────────────
const CHAPTER_COLOR = {
  "Platform":         "#e6c97a",
  "Seller Flow":      "#7ecfb0",
  "Buyer Flow":       "#7ab0e6",
  "Transaction Room": "#c4b5fd",
  "Family Sharing":   "#f9a8d4",
};

// ─── Step callout popup ───────────────────────────────────────────────────────
const mono = "'DM Mono',monospace";
const serif = "'Playfair Display',serif";
const sans  = "'Outfit','Inter',sans-serif";

function StepCallout({ text, x, y, arrow = "left", visible }) {
  const bg = "rgba(10,24,18,0.94)";
  const arrows = {
    left:  { left:-8,  top:"50%", transform:"translateY(-50%)", borderRight:`8px solid ${bg}`, borderTop:"6px solid transparent", borderBottom:"6px solid transparent" },
    right: { right:-8, top:"50%", transform:"translateY(-50%)", borderLeft:`8px solid ${bg}`,  borderTop:"6px solid transparent", borderBottom:"6px solid transparent" },
    up:    { top:-8,   left:"50%", transform:"translateX(-50%)", borderBottom:`8px solid ${bg}`, borderLeft:"6px solid transparent", borderRight:"6px solid transparent" },
    down:  { bottom:-8,left:"50%", transform:"translateX(-50%)", borderTop:`8px solid ${bg}`,   borderLeft:"6px solid transparent", borderRight:"6px solid transparent" },
  };
  return (
    <div style={{
      position:"absolute", left:`${x}%`, top:`${y}%`, zIndex:200, pointerEvents:"none",
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(-50%) scale(1)" : "translateY(-50%) scale(0.88)",
      transition: "opacity 0.4s ease 0.3s, transform 0.4s ease 0.3s",
    }}>
      <div style={{
        position:"relative", background:bg, color:"#7ecfb0",
        border:"1px solid rgba(126,207,176,0.3)", borderRadius:10,
        padding:"8px 13px 8px 10px", fontSize:11, fontFamily:mono,
        maxWidth:210, lineHeight:1.5,
        boxShadow:"0 6px 28px rgba(0,0,0,0.6)", display:"flex", alignItems:"flex-start", gap:8,
      }}>
        <div style={{ position:"absolute", width:0, height:0, ...arrows[arrow] }} />
        <span style={{ display:"inline-block", width:7, height:7, borderRadius:"50%", background:"#7ecfb0", flexShrink:0, animation:"pulse 1.8s infinite" }} />
        {text}
      </div>
    </div>
  );
}

// ─── Step sequence — 12 steps × 6 s = 72 s total ────────────────────────────
const STEPS = [
  {
    chapter: "Platform",
    title: "Buy. Sell. Negotiate. No agent required.",
    body: "6zero.ai is a direct buyer-to-seller platform. Sellers keep the 6% they'd give an agent. Buyers get AI fair-price estimates before they make a single offer.",
    seed: seedBuyer,
    url: () => `/?_d=${Date.now()}`,
    totalDuration: 6000,
    callouts: [
      { phase: 0, text: "I'm selling → AI-guided end-to-end sale", x: 30, y: 68, arrow: "right" },
      { phase: 1, text: "I'm a buyer → search + AI fair-price", x: 62, y: 48, arrow: "left" },
    ],
    phases: [0, 1],
    phaseDuration: 3000,
  },
  {
    chapter: "Seller Flow",
    title: "Just give us your address — we handle the rest",
    body: "The seller enters their property address. 6zero.ai instantly looks up public records — beds, baths, sqft, year built — so the seller never has to fill a form.",
    seed: clearSellerCurrent,
    url: () => `/sell?view=intake&_d=${Date.now()}`,
    totalDuration: 6000,
    callouts: [
      { phase: 0, text: "One address — no paperwork", x: 55, y: 42, arrow: "left" },
      { phase: 1, text: "Keep the 3% listing-agent fee", x: 55, y: 70, arrow: "left" },
    ],
    phases: [0, 1],
    phaseDuration: 3000,
  },
  {
    chapter: "Seller Flow",
    title: "AI pulls live comps and sets your price",
    body: "Claude searches recent sales within 0.5 miles, compares $/sqft, and returns a fair listing range with confidence level — in seconds. No agent opinion, just data.",
    seed: () => seedSeller(0),
    url: () => `/sell?_d=${Date.now()}`,
    totalDuration: 6000,
    callouts: [
      { phase: 0, text: "AI-generated fair-price range", x: 62, y: 38, arrow: "left" },
      { phase: 1, text: "Live comps cited — no black box", x: 62, y: 65, arrow: "left" },
    ],
    phases: [0, 1],
    phaseDuration: 3000,
  },
  {
    chapter: "Seller Flow",
    title: "ROI-ranked prep — the fixes that actually move price",
    body: "AI identifies under-$2k repairs that materially move sale price, ranked by priority. No over-spending. The vendor marketplace is one tap away to book the work.",
    seed: () => seedSeller(1),
    url: () => `/sell?_d=${Date.now()}`,
    totalDuration: 6000,
    callouts: [
      { phase: 0, text: "Ranked by return on investment", x: 65, y: 32, arrow: "left" },
      { phase: 1, text: "Cost + price lift for every item", x: 65, y: 60, arrow: "left" },
    ],
    phases: [0, 1],
    phaseDuration: 3000,
  },
  {
    chapter: "Seller Flow",
    title: "Vendor marketplace — book without back-and-forth",
    body: "AI-ranked vendors for your home type and ZIP code. See personalized quotes, pick a date and time slot, and confirm — all in one flow. Both parties get a confirmation email.",
    seed: seedBuyer,
    url: () => `/marketplace?_d=${Date.now()}`,
    totalDuration: 6000,
    callouts: [
      { phase: 0, text: "AI-ranked for your home + ZIP", x: 65, y: 30, arrow: "left" },
      { phase: 1, text: "Book directly — no phone tag", x: 65, y: 62, arrow: "left" },
    ],
    phases: [0, 1],
    phaseDuration: 3000,
  },
  {
    chapter: "Seller Flow",
    title: "Disclosure wizard — not a 30-page form",
    body: "AI generates 7 tailored plain-English questions for your specific property. Your answers are synthesized into TDS, SPQ, NHD, lead-paint, and HOA disclosures in the background.",
    seed: () => seedSeller(2, { disclosures: DEMO_DISCLOSURES_DATA }),
    url: () => `/sell?_d=${Date.now()}`,
    totalDuration: 6000,
    callouts: [
      { phase: 0, text: "7 Qs replaces a 30-page packet", x: 65, y: 22, arrow: "left" },
      { phase: 1, text: "AI synthesizes required legal docs", x: 65, y: 72, arrow: "left" },
    ],
    phases: [0, 1],
    phaseDuration: 3000,
  },
  {
    chapter: "Seller Flow",
    title: "Offer inbox — AI scores every offer automatically",
    body: "Each offer card shows price, financing type, verified pre-approval badge, contingencies, and an AI-generated close-probability score. Accept, counter, or decline in one tap.",
    seed: () => seedSeller(9, { offers: DEMO_OFFERS_DATA }),
    url: () => `/sell?_d=${Date.now()}`,
    totalDuration: 6000,
    callouts: [
      { phase: 0, text: "AI close-probability on every offer", x: 15, y: 48, arrow: "right" },
      { phase: 1, text: "Accept · Counter · Decline — one tap", x: 15, y: 72, arrow: "right" },
    ],
    phases: [0, 1],
    phaseDuration: 3000,
  },
  {
    chapter: "Buyer Flow",
    title: "Buyers search the market — no agent needed",
    body: "Live MLS listings on an interactive map. Price markers show every home. Buyers filter by price, beds, type. AI fair-price estimates load the moment they open any listing.",
    seed: seedBuyer,
    url: () => `/buy?demo=1&_d=${Date.now()}`,
    totalDuration: 6000,
    callouts: [
      { phase: 0, text: "Live listings on an interactive map", x: 72, y: 38, arrow: "left" },
      { phase: 1, text: "Filter by price, beds, or type", x: 72, y: 52, arrow: "left" },
    ],
    phases: [0, 1],
    phaseDuration: 3000,
  },
  {
    chapter: "Buyer Flow",
    title: "AI fair-price estimate — same number both sides see",
    body: "When a buyer opens any listing, the AI fair-price range loads automatically. Listed at $1.195M · within range. Both buyer and seller now negotiate from the same data-backed number.",
    seed: seedBuyer,
    url: () => `/buy?demo=1&autoselect=1&_d=${Date.now()}`,
    totalDuration: 6000,
    callouts: [
      { phase: 0, text: "AI fair-value estimate loads on open", x: 56, y: 50, arrow: "left" },
      { phase: 1, text: "Same number the seller saw", x: 56, y: 64, arrow: "left" },
    ],
    phases: [0, 1],
    phaseDuration: 3000,
  },
  {
    chapter: "Buyer Flow",
    title: "Pre-approval gate — verified before the offer lands",
    body: "Financed buyers upload their pre-approval letter. AI extracts lender, amount, and expiry in seconds. Sellers see the verification badge on every offer — no guesswork.",
    seed: seedBuyer,
    url: () => `/buy?demo=1&autooffer=1&_d=${Date.now()}`,
    totalDuration: 6000,
    callouts: [
      { phase: 0, text: "Offer pre-filled · $1.18M ready to send", x: 68, y: 38, arrow: "left" },
      { phase: 1, text: "Chase Bank · $1.3M pre-approved · AI-verified", x: 68, y: 54, arrow: "left" },
    ],
    phases: [0, 1],
    phaseDuration: 3000,
  },
  {
    chapter: "Transaction Room",
    title: "Accept → shared transaction room opens instantly",
    body: "One click to accept. A /tx room is created with a full milestone timeline to closing. Seller copies the link for the buyer. Both parties track every step in the same place.",
    seed: seedBuyer,
    url: () => `/tx/${DEMO_TX_ID}?token=${DEMO_TX_TOKEN}&_d=${Date.now()}`,
    totalDuration: 6000,
    callouts: [
      { phase: 0, text: "AI valuation visible to both sides", x: 62, y: 40, arrow: "right" },
      { phase: 1, text: "Click any milestone to mark it done", x: 34, y: 62, arrow: "left" },
    ],
    phases: [0, 1],
    phaseDuration: 3000,
  },
  {
    chapter: "Family Sharing",
    title: "Share your shortlist with family — no account needed",
    body: "Buyers share a link with family. Anyone enters their name and reacts: ❤️ Love · 👍 Like · 🤔 Maybe · ✗ Pass — with optional notes. All reactions are visible to the whole group.",
    seed: seedBuyer,
    url: () => `/share/${DEMO_SL_ID}?token=${DEMO_SL_TOKEN}&_d=${Date.now()}`,
    totalDuration: 6000,
    callouts: [
      { phase: 0, text: "No account needed — just enter a name", x: 68, y: 28, arrow: "left" },
      { phase: 1, text: "Reactions visible to the whole group", x: 68, y: 72, arrow: "left" },
    ],
    phases: [0, 1],
    phaseDuration: 3000,
  },
];

const CHAPTERS = [...new Set(STEPS.map((s) => s.chapter))];

// ─── Main component ───────────────────────────────────────────────────────────
export default function DemoApp() {
  const [stepIdx, setStepIdx]   = useState(0);
  const [phase, setPhase]       = useState(0);
  const [playing, setPlaying]   = useState(true);
  const [iframeReady, setIframeReady] = useState(false);

  const iframeRef  = useRef(null);
  const timerRef   = useRef(null);
  const phaseRef   = useRef(null);

  const step = STEPS[stepIdx];
  const chapterColor = CHAPTER_COLOR[step.chapter] || "#7ecfb0";
  const progress = (stepIdx / (STEPS.length - 1)) * 100;

  const navigateTo = useCallback((idx) => {
    const clamped = Math.max(0, Math.min(STEPS.length - 1, idx));
    const s = STEPS[clamped];
    setIframeReady(false);
    setPhase(0);
    setStepIdx(clamped);
    if (s.seed) s.seed();
    if (iframeRef.current) iframeRef.current.src = s.url();
  }, []);

  // Initial load
  useEffect(() => {
    const s = STEPS[0];
    if (s.seed) s.seed();
    if (iframeRef.current) iframeRef.current.src = s.url();
  }, []); // eslint-disable-line

  // Phase progression within a step
  useEffect(() => {
    if (!playing) return;
    const phases = step.phases || [0];
    const dur = step.phaseDuration || 3000;
    phaseRef.current && clearTimeout(phaseRef.current);
    let t = 0;
    phases.forEach((_, i) => {
      if (i === 0) return;
      t += dur;
      phaseRef.current = setTimeout(() => setPhase(i), t);
    });
    return () => phaseRef.current && clearTimeout(phaseRef.current);
  }, [stepIdx, playing]); // eslint-disable-line

  // Step auto-advance
  useEffect(() => {
    if (!playing) return;
    timerRef.current && clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (stepIdx < STEPS.length - 1) navigateTo(stepIdx + 1);
      else setPlaying(false);
    }, step.totalDuration);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [stepIdx, playing, navigateTo, step.totalDuration]);

  // Keyboard nav
  useEffect(() => {
    const h = (e) => {
      if (e.key === "ArrowRight") navigateTo(stepIdx + 1);
      if (e.key === "ArrowLeft")  navigateTo(stepIdx - 1);
      if (e.key === " ")          { e.preventDefault(); setPlaying((p) => !p); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [stepIdx, navigateTo]);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Outfit:wght@400;600;700&family=Playfair+Display:ital,wght@0,700;1,700&family=Inter:wght@400;500;600&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        body{background:#050a06;font-family:'Inter','Outfit',sans-serif;overflow:hidden}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.35}}
        @keyframes fadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
        @keyframes spin{to{transform:rotate(360deg)}}
      `}</style>

      <div style={{ height:"100vh", display:"flex", flexDirection:"column", background:"#050a06" }}>

        {/* Top bar */}
        <div style={{ background:"#0a100c", borderBottom:"1px solid #1c2b25", padding:"9px 24px", display:"flex", alignItems:"center", gap:14, flexShrink:0 }}>
          <span style={{ fontFamily:mono, fontSize:13, fontWeight:700, color:"#7ecfb0", letterSpacing:"0.2em" }}>6zero.ai</span>
          <span style={{ fontFamily:mono, fontSize:10, color:"#3a5a4a", letterSpacing:"0.14em" }}>PRODUCT DEMO</span>
          <div style={{ flex:1, height:4, background:"#1c2b25", borderRadius:4, margin:"0 12px" }}>
            <div style={{ height:"100%", borderRadius:4, background:chapterColor, width:`${progress}%`, transition:"width 0.6s ease" }} />
          </div>
          <span style={{ fontFamily:mono, fontSize:10, color:"#3a5a4a" }}>{stepIdx + 1} / {STEPS.length}</span>
        </div>

        {/* Chapter tabs */}
        <div style={{ background:"#0e1410", borderBottom:"1px solid #1c2b25", padding:"0 24px", display:"flex", gap:4, flexShrink:0 }}>
          {CHAPTERS.map((ch) => {
            const isCurrent = ch === step.chapter;
            return (
              <div key={ch}
                style={{ padding:"8px 14px", fontSize:11, fontFamily:mono, letterSpacing:"0.08em", cursor:"pointer", whiteSpace:"nowrap",
                  color: isCurrent ? CHAPTER_COLOR[ch] : "#3a5a4a",
                  borderBottom: isCurrent ? `2px solid ${CHAPTER_COLOR[ch]}` : "2px solid transparent",
                  transition:"color 0.2s" }}
                onClick={() => { const idx = STEPS.findIndex((s) => s.chapter === ch); navigateTo(idx); }}>
                {ch}
              </div>
            );
          })}
        </div>

        {/* Main area */}
        <div style={{ flex:1, display:"flex", minHeight:0 }}>

          {/* Narrator */}
          <div style={{ width:300, background:"#0a100c", borderRight:"1px solid #1c2b25", padding:"28px 22px", display:"flex", flexDirection:"column", flexShrink:0, overflowY:"auto" }}>
            <div style={{ fontFamily:mono, fontSize:10, color:chapterColor, letterSpacing:"0.14em", textTransform:"uppercase", marginBottom:10, animation:"fadeIn 0.4s ease" }}>
              {step.chapter}
            </div>
            <h2 style={{ fontFamily:serif, fontSize:20, color:"#cfe6da", fontWeight:700, lineHeight:1.35, marginBottom:14, animation:"fadeIn 0.4s ease 0.05s both" }}>
              {step.title}
            </h2>
            <p style={{ fontFamily:sans, fontSize:13, color:"#5a8a78", lineHeight:1.75, animation:"fadeIn 0.4s ease 0.1s both" }}>
              {step.body}
            </p>

            {/* Step dots */}
            <div style={{ display:"flex", flexWrap:"wrap", gap:5, marginTop:28 }}>
              {STEPS.map((s, i) => (
                <div key={i} onClick={() => navigateTo(i)}
                  style={{ width: i === stepIdx ? 20 : 8, height:8, borderRadius:4,
                    background: i === stepIdx ? chapterColor : i < stepIdx ? "#2a4035" : "#1c2b25",
                    cursor:"pointer", transition:"all 0.3s ease", flexShrink:0 }} />
              ))}
            </div>

            {/* Controls */}
            <div style={{ marginTop:"auto", paddingTop:32 }}>
              <div style={{ display:"flex", gap:8, marginBottom:10 }}>
                <button onClick={() => navigateTo(stepIdx - 1)} disabled={stepIdx === 0}
                  style={{ flex:1, background:"#0e1410", color:"#5a8a78", border:"1px solid #1c2b25", borderRadius:8, padding:"9px", fontSize:12, cursor:"pointer", fontFamily:mono, opacity: stepIdx === 0 ? 0.4 : 1 }}>
                  ← Back
                </button>
                <button onClick={() => setPlaying((p) => !p)}
                  style={{ flex:1, background:"#1c2b25", color:"#cfe6da", border:"none", borderRadius:8, padding:"9px", fontSize:12, cursor:"pointer", fontFamily:mono }}>
                  {playing ? "⏸ Pause" : "▶ Play"}
                </button>
                <button onClick={() => navigateTo(stepIdx + 1)} disabled={stepIdx === STEPS.length - 1}
                  style={{ flex:1, background:"#1c5448", color:"#7ecfb0", border:"none", borderRadius:8, padding:"9px", fontSize:12, cursor:"pointer", fontFamily:mono, opacity: stepIdx === STEPS.length - 1 ? 0.4 : 1 }}>
                  Next →
                </button>
              </div>
              <div style={{ fontSize:10, color:"#2a4035", fontFamily:mono, textAlign:"center", letterSpacing:"0.08em" }}>
                ← → keys · space to pause
              </div>
            </div>
          </div>

          {/* iframe area */}
          <div style={{ flex:1, display:"flex", flexDirection:"column", minHeight:0, background:"#0a0f0b", position:"relative" }}>

            {/* URL bar */}
            <div style={{ background:"#0e1410", borderBottom:"1px solid #1c2b25", padding:"6px 16px", display:"flex", alignItems:"center", gap:10, flexShrink:0 }}>
              <div style={{ display:"flex", gap:5 }}>
                {["#e67a7a","#e6c97a","#7ecfb0"].map((c) => (
                  <div key={c} style={{ width:10, height:10, borderRadius:"50%", background:c, opacity:0.6 }} />
                ))}
              </div>
              <div style={{ flex:1, background:"#0a100c", border:"1px solid #1c2b25", borderRadius:6, padding:"4px 12px", fontFamily:mono, fontSize:11, color:"#3a5a4a" }}>
                6zero.ai
              </div>
              {!iframeReady && (
                <div style={{ width:14, height:14, border:"2px solid #1c2b25", borderTop:`2px solid ${chapterColor}`, borderRadius:"50%", animation:"spin 0.8s linear infinite", flexShrink:0 }} />
              )}
            </div>

            {/* iframe + callout overlay */}
            <div style={{ flex:1, position:"relative", overflow:"hidden" }}>
              <iframe
                ref={iframeRef}
                onLoad={() => setIframeReady(true)}
                style={{
                  width:"100%", height:"100%", border:"none", display:"block",
                  opacity: iframeReady ? 1 : 0,
                  transition:"opacity 0.3s ease",
                }}
                title="6zero.ai demo"
              />
              {/* Loading placeholder while iframe loads */}
              {!iframeReady && (
                <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", background:"#0e1410" }}>
                  <div style={{ fontFamily:mono, fontSize:12, color:"#3a5a4a", letterSpacing:"0.1em" }}>Loading…</div>
                </div>
              )}
              {/* Callout overlay — pointer-events none so the real app stays interactive */}
              <div style={{ position:"absolute", inset:0, pointerEvents:"none", zIndex:100 }}>
                {(step.callouts || []).map((c, i) => (
                  <StepCallout
                    key={`${stepIdx}-${i}`}
                    text={c.text}
                    x={c.x}
                    y={c.y}
                    arrow={c.arrow || "left"}
                    visible={iframeReady && phase >= c.phase && phase < (c.phaseEnd ?? Infinity)}
                  />
                ))}
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
